import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { useNavigate } from "react-router-dom";
import { headerSty, contentSty } from "../table-styles";
import { formatDateTime, formatTextType } from "../../utils/url-id";

export default function JobBoardTable({ data, loading }: any) {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };

  const navigate = useNavigate();
  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open

  const handleToggleOpen = (rowId: number) => {
    setOpenRow((prevOpenRow) => (prevOpenRow === rowId ? null : rowId));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        my: 1,
        background: theme.palette.common.white,
        minHeight: 400,
        borderRadius: "16px",
        border: 0,
        boxShadow: "none",
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          boxShadow: "none",
          my: 2,
          paddingX: 2,
          border: 0,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2, border: 0 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles }}>Job Title</TableCell>
            <TableCell sx={{ ...headStyles }}>Department</TableCell>
            <TableCell sx={{ ...headStyles }}>Job Type</TableCell>
            <TableCell sx={{ ...headStyles }}>Emp. Type</TableCell>
            <TableCell sx={{ ...headStyles }}>Applications</TableCell>
            <TableCell sx={{ ...headStyles }}> Date/Time</TableCell>
            <TableCell sx={{ ...headStyles, width: '10%' }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && data.map((row: any, index: number) => (
            <TableRow
              key={row?._id}
              onClick={() => navigate(`/job-board/${row?._id}`)}
            >
              <TableCell sx={contentStyles}>{row.job_title}</TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.department ?? "?"}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatTextType(row.job_type)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatTextType(row.employment_type)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.stats?.number_of_applicants ?? 0}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDateTime(row?.created_at)}
              </TableCell>
             
              <TableCell sx={{ ...contentStyles }}>
                <JobEmpStatus status={row?.status ?? ""} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data?.length === 0 && !loading && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No jobs Available.
          </Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}


export const JobEmpStatus: React.FC<{ status: 'pending' | 'closed' | 'active' | string }> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          color: theme.palette.warning.main,
          backgroundColor: '#ffac336b'
        };
      case 'closed':
        return {
          color: theme.palette.error.main,
          backgroundColor: '#f6685e6b',
        };
      case 'active':
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.error.main,
          backgroundColor: '#f6685e6b',
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: 'auto' }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: 'Lexend',
          textTransform: 'uppercase',
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};


const TableDots: React.FC<{
  rowId: number;
  isOpen: boolean;
  toggleOpen: (id: number) => void;
}> = ({ rowId, isOpen, toggleOpen }) => {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const handleOpen = () => {
    setOpen(!open);
  };
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
          width: "20px",
        }}
      >
        <Dot />
      </Box>
      {open && (
        <Box
          sx={{
            background: "#b0bee3",
            position: "absolute",
            top: 18,
            right: 0,
            borderRadius: "12px",
            padding: "8px 12px",
            zIndex: 10,
            width: 95,
            cursor: "pointer",
          }}
          onClick={() => navigate(`/job-board/${rowId}`)}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#1E1E1E",
            }}
          >
            View Post
          </Typography>
        </Box>
      )}
    </Box>
  );
};
